/**
 * First-party pageview tracking for the app itself.
 *
 * The presentation site loads a script served by the API; this is the in-app
 * equivalent, so both ends of the funnel land in the same table and can be
 * compared. Everything is fire-and-forget: a failure here must never be
 * visible to the user, so nothing throws and nothing is awaited.
 */

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";
const ENDPOINT = `${API.replace(/\/$/, "")}/web-analytics/collect`;

/** Per-tab id, gone when the tab closes — not a persistent identifier. */
function sessionId(): string | null {
  try {
    let sid = sessionStorage.getItem("_ig_sid");
    if (!sid) {
      sid = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      sessionStorage.setItem("_ig_sid", sid);
    }
    return sid;
  } catch {
    // Private mode or storage disabled: still count the pageview, just without
    // being able to group it into a session.
    return null;
  }
}

function uid(): string {
  return `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function post(body: Record<string, any>): void {
  try {
    const payload = JSON.stringify(body);
    // text/plain keeps this a "simple" request, so the browser sends it without
    // a CORS preflight. The collector parses text and JSON alike.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "text/plain;charset=UTF-8" }));
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: payload,
      keepalive: true,
      mode: "no-cors",
    }).catch(() => {});
  } catch {
    /* analytics must never break the app */
  }
}

// State for the current pageview, so the duration ping can update the row the
// view beacon created instead of inserting a second one.
let currentId: string | null = null;
let currentPath: string | null = null;
let shownAt = Date.now();
let reported = false;

function query(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** Send how long the page just left was on screen. Only ever once per view. */
export function trackLeave(): void {
  if (!currentId || reported) return;
  reported = true;
  post({ eid: currentId, site: "app", path: currentPath, duration_ms: Date.now() - shownAt });
}

/**
 * Record a pageview. `userId` is the signed-in user when there is one — it is
 * what separates "traffic" from "usage" in the report.
 */
export function trackPageview(path: string, userId?: string | null): void {
  if (path === currentPath) return;

  trackLeave();

  currentPath = path;
  currentId = uid();
  shownAt = Date.now();
  reported = false;

  post({
    eid: currentId,
    site: "app",
    path,
    host: window.location.hostname,
    // Only the entry into the app is an interesting referrer; once inside,
    // document.referrer is our own previous page, which the collector discards
    // anyway by comparing hosts.
    referrer: document.referrer || null,
    session_id: sessionId(),
    utm_source: query("utm_source"),
    utm_medium: query("utm_medium"),
    utm_campaign: query("utm_campaign"),
    user_id: userId || null,
  });
}

let bound = false;

/** Flush the open pageview when the tab is hidden or closed. Idempotent. */
export function bindAnalyticsLifecycle(): void {
  if (bound || typeof window === "undefined") return;
  bound = true;
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") trackLeave();
  });
  // Safari does not reliably fire visibilitychange on navigation away.
  window.addEventListener("pagehide", trackLeave);
}
